import React, { useState, useContext } from 'react';
import { AuthContext } from '../../components/AuthContext';
import Swal from 'sweetalert2';
import api from '../../api';
import { FaFileExcel, FaFilePdf } from 'react-icons/fa';

const toISODate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const getCurrentFinancialYear = () => {
    const now = new Date();
    const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return {
        start: `${startYear}-04-01`,
        end: `${startYear + 1}-03-31`
    };
};

const getPresetRange = (preset) => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    switch (preset) {
        case 'today':
            return { start: toISODate(now), end: toISODate(now) };
        case 'yesterday': {
            const d = new Date(y, m, now.getDate() - 1);
            return { start: toISODate(d), end: toISODate(d) };
        }
        case 'current-month':
            return { start: toISODate(new Date(y, m, 1)), end: toISODate(now) };
        case 'last-month':
            return { start: toISODate(new Date(y, m - 1, 1)), end: toISODate(new Date(y, m, 0)) };
        case 'current-quarter': {
            const qs = Math.floor(m / 3) * 3;
            return { start: toISODate(new Date(y, qs, 1)), end: toISODate(now) };
        }
        case 'last-quarter': {
            const qs = Math.floor(m / 3) * 3;
            return { start: toISODate(new Date(y, qs - 3, 1)), end: toISODate(new Date(y, qs, 0)) };
        }
        default:
            return getCurrentFinancialYear();
    }
};

const EXPORT_PRESETS = [
    { value: 'today', label: 'Today (till now)' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'current-month', label: 'Current Month' },
    { value: 'last-month', label: 'Last Month' },
    { value: 'current-quarter', label: 'Current Quarter' },
    { value: 'last-quarter', label: 'Last Quarter' },
    { value: 'current-fy', label: 'Current Financial Year' },
    { value: 'custom', label: 'Custom Date Range' }
];

const SHEET_LABELS = {
    summary: 'Salesperson Summary',
    do: 'DO Details',
    qn: 'Quotation New Details',
    tq: 'Tally Quotation Details',
    visitor: 'Visitor Details',
    gr: 'GR Details'
};

const DEFAULT_SHEET_ORDER = ['summary', 'visitor', 'qn', 'tq', 'do', 'gr'];

export default function Dashboard({ showHeader = true } = {}) {
    const { user } = useContext(AuthContext);
    const [exporting, setExporting] = useState(false);
    const [exportingSummary, setExportingSummary] = useState(null);
    const [rangePreset, setRangePreset] = useState('current-fy');
    const [dateRange, setDateRange] = useState(getCurrentFinancialYear);
    const [includeItems, setIncludeItems] = useState(true);
    const [sheetOrder, setSheetOrder] = useState(DEFAULT_SHEET_ORDER);

    const moveSheet = (index, direction) => {
        setSheetOrder((prev) => {
            const target = index + direction;
            if (target < 0 || target >= prev.length) return prev;
            const next = [...prev];
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const getCompanyId = () => {
        const company = user?.user?.company;
        if (!company) return null;
        if (typeof company === 'string') return company;
        return company._id || company.id || null;
    };

    const handleExportNegativeStock = async () => {
        const companyId = getCompanyId();
        if (!companyId) {
            Swal.fire('Error', 'Company not found', 'error');
            return;
        }
        try {
            setExporting(true);
            const response = await api.get(`/tally/items/export-negative-stock/${companyId}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `negative-stock-items-${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            Swal.fire('Success', 'Negative stock items exported successfully', 'success');
        } catch (error) {
            if (error.response?.status === 404) {
                Swal.fire('Info', 'No items with negative stock found', 'info');
            } else {
                Swal.fire('Error', 'Failed to export negative stock items', 'error');
            }
        } finally {
            setExporting(false);
        }
    };

    const handleExportSalespersonSummary = async (format) => {
        const companyId = getCompanyId();
        if (!companyId) {
            Swal.fire('Error', 'Company not found', 'error');
            return;
        }
        const extension = format === 'pdf' ? 'pdf' : 'xlsx';
        try {
            setExportingSummary(format);
            const range = rangePreset === 'custom' ? dateRange : getPresetRange(rangePreset);
            const response = await api.get('/reports/salesperson-summary/export', {
                params: {
                    companyId,
                    startDate: range.start,
                    endDate: range.end,
                    format,
                    includeItems: includeItems ? 'true' : 'false',
                    sheetOrder: sheetOrder.join(',')
                },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const disposition = response.headers?.['content-disposition'] || '';
            const nameMatch = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
            const companyLabel = String(user?.user?.company?.name || 'Company').trim().replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
            const fallbackName = `Salesperson-Summary_${companyLabel}_${range.start}_to_${range.end}.${extension}`;
            link.setAttribute('download', nameMatch ? decodeURIComponent(nameMatch[1]) : fallbackName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            Swal.fire('Success', `Salesperson summary ${extension.toUpperCase()} exported successfully`, 'success');
        } catch (error) {
            if (error.response?.status === 404) {
                Swal.fire('Info', 'No data found for the selected period', 'info');
            } else {
                Swal.fire('Error', 'Failed to export salesperson summary', 'error');
            }
        } finally {
            setExportingSummary(null);
        }
    };

    return (
        <div className="flex flex-col items-center justify-center h-screen">
            {showHeader && (
                <>
                    <h1 className="text-4xl font-bold mb-4 text-gray-800 dark:text-gray-100">Welcome, {user.name}</h1>
                    <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">You are logged in as a company head.</p>
                </>
            )}
            <div className="flex items-end gap-3 mb-4 flex-wrap justify-center">
                <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Period</label>
                    <select
                        value={rangePreset}
                        onChange={(e) => setRangePreset(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 dark:bg-slate-800 dark:border-slate-600 dark:text-gray-100"
                    >
                        {EXPORT_PRESETS.map((preset) => (
                            <option key={preset.value} value={preset.value}>{preset.label}</option>
                        ))}
                    </select>
                </div>
                {rangePreset === 'custom' && (
                    <>
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
                            <input
                                type="date"
                                value={dateRange.start}
                                onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
                                className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 dark:bg-slate-800 dark:border-slate-600 dark:text-gray-100"
                            />
                        </div>
                        <div className="flex flex-col">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
                            <input
                                type="date"
                                value={dateRange.end}
                                onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
                                className="px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800 dark:bg-slate-800 dark:border-slate-600 dark:text-gray-100"
                            />
                        </div>
                    </>
                )}
            </div>
            <div className="w-full max-w-3xl mb-4 px-4">
                <label className="flex items-center justify-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    <input
                        type="checkbox"
                        checked={includeItems}
                        onChange={(e) => setIncludeItems(e.target.checked)}
                        className="h-4 w-4"
                    />
                    Include item details (line items)
                </label>
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">Sheet order</div>
                <ul className="space-y-1">
                    {sheetOrder.map((key, index) => (
                        <li key={key} className="flex items-center justify-between gap-3 bg-white/70 dark:bg-slate-800/60 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-1.5">
                            <span className="text-sm text-gray-800 dark:text-gray-100">{index + 1}. {SHEET_LABELS[key]}</span>
                            <span className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => moveSheet(index, -1)}
                                    disabled={index === 0}
                                    aria-label="Move sheet up"
                                    className="px-2 py-0.5 rounded border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                >↑</button>
                                <button
                                    type="button"
                                    onClick={() => moveSheet(index, 1)}
                                    disabled={index === sheetOrder.length - 1}
                                    aria-label="Move sheet down"
                                    className="px-2 py-0.5 rounded border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                >↓</button>
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="flex items-center gap-3">
                <button
                    onClick={() => handleExportSalespersonSummary('excel')}
                    disabled={!!exportingSummary}
                    className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition dark:bg-green-700 dark:hover:bg-green-600"
                >
                    <FaFileExcel className="text-lg" />
                    {exportingSummary === 'excel' ? 'Exporting...' : 'Export Excel'}
                </button>
                <button
                    onClick={() => handleExportSalespersonSummary('pdf')}
                    disabled={!!exportingSummary}
                    className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition dark:bg-blue-700 dark:hover:bg-blue-600"
                >
                    <FaFilePdf className="text-lg" />
                    {exportingSummary === 'pdf' ? 'Exporting...' : 'Export PDF'}
                </button>
                <button
                    onClick={handleExportNegativeStock}
                    disabled={exporting}
                    className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition dark:bg-red-700 dark:hover:bg-red-600"
                >
                    <FaFileExcel className="text-lg" />
                    {exporting ? 'Exporting...' : 'Export Negative Stock Items'}
                </button>
            </div>
        </div>
    );
}
